<template>
  <div id="list" :class="{ loading: isLoading }">
    <div class="content">
      <h3>Trips</h3>
      <p class="infomsg">The list shows all trips that belong to you or teams that you administer. You may use the filters below to view just your own trips or your team's trips. You may also filter by active or previous trips. Lastly, you may search the people, places and providers within the trips.</p>
      <div class="form-row">
        <div class="form-col">
          <select v-model="owner">
            <option value="all">All Owners</option>
            <option value="yours">Yours</option>
            <option value="team">Team</option>
            <option v-for="o in props.organizations" :key="o.id" :value="o.id">{{ o.name }}</option>
          </select>
        </div>
        <div class="form-col">
          <select v-model="status">
            <option value="all">Active &amp; Previous</option>
            <option value="act">Active</option>
            <option value="aun">Active - Unbooked</option>
            <option value="abk">Active - Booked</option>
            <option value="prv">Previous</option>
          </select>
        </div>
        <div class="form-col search">
          <span class="icon">&#x1f50e;</span>
          <input v-model="search" type="text" class="search" placeholder="Search people, places or providers..." />
        </div>
      </div>
      <div :class="{ loading: isSearching }">
      <table>
        <thead>
          <tr>
            <th v-if="hasCustomIdentifiers" colspan="2">ID</th>
            <th class="left" colspan="2">Place</th>
            <th class="left">Start</th>
            <th class="left">End</th>
            <th>Owner</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(d, i) in dataFiltered" :key="i" class="clickable-row" @click="openTrip(d.id)">
            <td v-if="hasCustomIdentifiers" colspan="2">{{ d.identifier }}</td>
            <td class="left" colspan="2">{{ d.places || "Unknown" }}</td>
            <td class="left nowrap">{{ d.start ? d.start.substring(0, 10) : "N/A" }}</td>
            <td class="left nowrap">{{ d.end ? d.end.substring(0, 10) : "N/A" }}</td>
            <td>
              {{ d.customer }}
            </td>
            <td class="nowrap" :class="{ red: d.needs_approval }">
              {{ tripStatus(d) }}
            </td>
          </tr>
          <tr v-if="dataFiltered.length == 0">
            <td :colspan="hasCustomIdentifiers ? 8 : 6" class="table-msg">No results</td>
          </tr>
        </tbody>
      </table>
      <div v-if="dataFiltered.length >= 1000" class="table-msg">Max 1000 results. Please use the search to find specific trips.</div>
      <div v-else-if="dataFiltered.length == 1" class="table-msg">{{ dataFiltered.length }} match found</div>
      <div v-else-if="dataFiltered.length > 1" class="table-msg">{{ dataFiltered.length }} matches found</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import useApi from "../utils/apiCalls";

const router = useRouter();
const openTrip = tripId => window.open(router.resolve({ name: "trip", params: { tripId } }).href, "_blank");

const emit = defineEmits(["error"]);
const props = defineProps(["organizations"]);

const { apiGetRequest } = useApi(emit);

const data = ref([]);
const search = ref("");
const searchResults = ref(null);
const searchTimeout = ref(null);
const isLoading = ref(0);
const isSearching = ref(false);
const owner = ref("all");
const status = ref("all");

const dataFiltered = computed(() => {
  if (searchResults.value !== null) return searchResults.value;

  return data.value
    .filter(row => {
      let ownerMatch = false;
      if (owner.value === "all") ownerMatch = true;
      else if (owner.value === "yours") ownerMatch = row.is_owner;
      else if (owner.value === "team") ownerMatch = !row.is_owner;
      else ownerMatch = row.organization_id == owner.value;
      if (!ownerMatch) return false;

      let statusMatch = false;
      if (status.value === "all") statusMatch = true;
      else if (status.value === "act") statusMatch = row.is_active;
      else if (status.value === "aun") statusMatch = row.is_active && !row.is_booked && !row.is_deposit;
      else if (status.value === "abk") statusMatch = row.is_active && (row.is_booked || row.is_deposit);
      else if (status.value === "prv") statusMatch = !row.is_active;
      if (!statusMatch) return false;

      return true;
    })
    // Float trips awaiting the customer's approval to the top; preserve the
    // server's trip_started DESC order within each group (stable sort).
    .sort((a, b) => (b.needs_approval ? 1 : 0) - (a.needs_approval ? 1 : 0));
});

const hasCustomIdentifiers = computed(() => {
  return dataFiltered.value.some(trip => trip.identifier && trip.identifier !== trip.id.toString());
});

watch(search, val => {
  clearTimeout(searchTimeout.value);
  if (val.length < 3) {
    searchResults.value = null;
    return;
  }
  searchTimeout.value = setTimeout(() => {
    isSearching.value = true;
    apiGetRequest({
      path: `/search/trips?query=${encodeURIComponent(val)}`,
      onSuccess: rsp => {
        searchResults.value = rsp || [];
        owner.value = "all";
        status.value = "all";
        isSearching.value = false;
      },
    });
  }, 300);
});

const tripStatus = row => {
  if (row.needs_approval) return "Needs Approval";
  if (row.is_active && row.is_booked) return "Active - Booked";
  if (row.is_active && row.is_deposit) return "Active - Deposit Paid";
  if (row.is_active && !row.is_booked) return "Active - Unbooked";
  if (!row.is_active) return "Previous";
  return "Unknown";
};

onMounted(() => {
  document.title = "Flightfox - Trips";
  isLoading.value++;
  apiGetRequest({
    path: "/reports/trips",
    onSuccess: rsp => {
      data.value = rsp;
      isLoading.value--;
    },
  });
});
</script>

<style lang="less">
#list {
  .search {
    .icon {
      display: block;
      font-size: 1.5rem;
      line-height: 3.625rem;
      margin-left: 0.75rem;
      position: absolute;
      z-index: 1;
    }

    input {
      text-indent: 1.75rem;
      width: 100%;
    }
  }

  .max {
    display: block;
    margin-top: 1rem;
    padding-top: 1rem;
    text-align: center;
  }

  table th {
    background: @bg;
    position: -webkit-sticky; /* Safari & IE */
    position: sticky;
    top: 0;
  }
}
</style>
